import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  cureElectionCategory,
  cureElectionFromOffer,
  financingLoanAmount,
  financingTermsFromOffer,
  getContingencyOptions,
  leadPaintElectionFromOffer,
  leadPaintElectionMode,
  normalizeContingencies,
  normalizeContingencyDeadlines,
  offerElectionError,
  offerTermsCategory,
  titlePeriodsFromOffer,
} from '../contingencyCatalog';
import { dateOnlyInDays, toDateOnly } from '../utils';
import {
  OFFER_DEFAULT_VALID_DAYS,
  getDefaultExpiresOn,
  getExpirationError,
} from '../offerExpiration';

const initialFormFor = (buyerId) => ({
  property_id: '',
  buyer_id: buyerId || null,
  seller_id: null,
  offer_price: 0,
  down_payment_percent: 20,
  closing_date: toDateOnly(new Date(Date.now() + 45 * 24 * 60 * 60 * 1000)),
  // Last day the seller can accept or counter. The deadline is 11:59 PM that
  // day in the PROPERTY's timezone; the backend computes the instant.
  expires_on: dateOnlyInDays(OFFER_DEFAULT_VALID_DAYS),
  // Nothing is pre-selected: a contingency weakens the offer, so the buyer has
  // to opt in rather than discover later that we added one for them (QA T-39).
  contingencies: {
    other: '',
  },
  earnest_money: '',
  additional_terms: '',
});

// Rate used for the monthly P&I estimate. It is a buyer-side what-if only:
// kept out of `form` so it is never submitted with the offer.
export const DEFAULT_APR_PERCENT = '7';

/** Monthly principal & interest on a 30-yr fixed loan at `aprPercent`. */
export function monthlyPayment(principal, aprPercent) {
  const months = 360;
  if (!(principal > 0)) return 0;
  const monthlyRate = (Number(aprPercent) || 0) / 100 / 12;
  if (monthlyRate <= 0) return principal / months;
  const growth = Math.pow(1 + monthlyRate, months);
  return (principal * monthlyRate * growth) / (growth - 1);
}

const STEP_ERRORS = {
  0: 'Enter an offer price greater than $0',
  1: 'Down payment must be between 0% and 100%',
};

export function useOfferForm({ buyerId, property }) {
  const [form, setForm] = useState(() => initialFormFor(buyerId));
  const [step, setStep] = useState(0);
  const [aprPercent, setAprPercent] = useState(DEFAULT_APR_PERCENT);

  // Pre-fill from property when it changes (truthy).
  useEffect(() => {
    if (!property) return;
    setForm(prev => ({
      ...prev,
      property_id: property.id,
      seller_id: property.seller_id || null,
      offer_price: property.price || property.list_price || 0,
      // Re-anchor the default to the property's calendar.
      expires_on: getDefaultExpiresOn(prev.closing_date, property.timezone),
    }));
    setStep(0);
  }, [property]);

  // Keep buyer_id in sync if it arrives later.
  useEffect(() => {
    if (buyerId == null) return;
    setForm(prev => (prev.buyer_id === buyerId ? prev : { ...prev, buyer_id: buyerId }));
  }, [buyerId]);

  const setField = useCallback((name, value) => {
    setForm(prev => {
      if (name.includes('.')) {
        const [head, tail] = name.split('.');
        return { ...prev, [head]: { ...prev[head], [tail]: value } };
      }
      return { ...prev, [name]: value };
    });
  }, []);

  const reset = useCallback(() => {
    setForm(initialFormFor(buyerId));
    setAprPercent(DEFAULT_APR_PERCENT);
    setStep(0);
  }, [buyerId]);

  const setFromCounter = useCallback((offer) => {
    if (!offer) return;
    setForm(prev => ({
      ...prev,
      property_id: offer.property_id ?? prev.property_id,
      seller_id: offer.seller_id ?? prev.seller_id,
      offer_price: offer.offer_price ?? prev.offer_price,
      down_payment_percent: offer.down_payment_percent ?? prev.down_payment_percent,
      closing_date: prev.closing_date,
      // Round-trip whatever the original offer carried — a counter must not
      // silently drop or re-add terms the parties already negotiated.
      contingencies: offer.contingencies
        ? {
            ...normalizeContingencies(offer.contingencies),
            ...normalizeContingencyDeadlines(offer.contingencies),
            ...leadPaintElectionFromOffer(offer.contingencies),
            ...cureElectionFromOffer(offer.contingencies),
            ...financingTermsFromOffer(offer.contingencies),
            ...titlePeriodsFromOffer(offer.contingencies),
            other: offer.contingencies.other || '',
          }
        : prev.contingencies,
    }));
  }, []);

  const derived = useMemo(() => {
    const listPrice = property?.price || property?.list_price || 0;
    const offerPrice = Number(form.offer_price) || 0;
    const dpPct = Number(form.down_payment_percent) || 0;
    const dpAmount = (offerPrice * dpPct) / 100;
    // The amount the financing clause prints: price minus down payment,
    // rounded down to the whole dollar (the server's rule; QA N13).
    const loanAmount = financingLoanAmount(form.offer_price, form.down_payment_percent);
    const financedAmount = loanAmount || 0;
    // A blank rate (mid-edit) falls back to the default instead of reading as 0%.
    const aprUsed = aprPercent === '' ? DEFAULT_APR_PERCENT : aprPercent;
    const monthlyPI = monthlyPayment(financedAmount, aprUsed);
    const earnest = form.earnest_money ? Number(form.earnest_money) : 0;
    const deltaVsList = listPrice && offerPrice ? offerPrice - listPrice : 0;
    const deltaPct = listPrice && offerPrice ? ((offerPrice / listPrice) - 1) * 100 : 0;
    return {
      offerPrice,
      dpPct,
      dpAmount,
      financedAmount,
      loanAmount,
      monthlyPI,
      aprUsed,
      listPrice,
      deltaVsList,
      deltaPct,
      earnest,
    };
  }, [form, property, aprPercent]);

  // Terms step: any deadline typed must be 1-365 days, and on every WI offer
  // (vacant land, residential, condo) each selected contingency needs one
  // (template 7(a), legal L4 §9.7; user decision). The options are the ones
  // TermsStep renders, so it is required exactly where it is asked. Where the
  // lead-paint election is asked (pre-1978 or unknown-year WI homes), the
  // buyer must choose inspect or waive before continuing. On every WI offer
  // with a curable contingency selected, the buyer must make the 7(d) cure
  // election (contingency-notices-copy.v1.md §11). Every WI offer needs both
  // title periods, and its financing contingency needs the loan terms and a
  // down payment below 100% (QA N13; copy file §10).
  const contingencyOptions = useMemo(() => getContingencyOptions(property), [property]);
  const leadPaintMode = useMemo(() => leadPaintElectionMode(property), [property]);
  const cureCategory = useMemo(() => cureElectionCategory(property), [property]);
  const termsCategory = useMemo(() => offerTermsCategory(property), [property]);
  const deadlineError = offerElectionError(form.contingencies, contingencyOptions, leadPaintMode, cureCategory, {
    category: termsCategory,
    downPaymentPercent: form.down_payment_percent,
  });
  // One message at a time: closing date, then the offer's acceptance deadline,
  // then the contingency elections.
  const termsError = !form.closing_date
    ? 'Choose a closing date'
    : getExpirationError(form.expires_on, form.closing_date, property?.timezone)
      || deadlineError
      || null;

  const canAdvance = useMemo(() => [
    derived.offerPrice > 0,
    derived.dpPct >= 0 && derived.dpPct <= 100,
    !termsError,
    true,
  ], [derived.offerPrice, derived.dpPct, termsError]);

  const errors = useMemo(() => {
    const out = {};
    canAdvance.forEach((ok, i) => {
      if (!ok && STEP_ERRORS[i]) out[i] = STEP_ERRORS[i];
    });
    if (termsError) out[2] = termsError;
    return out;
  }, [canAdvance, termsError]);

  return {
    form,
    setField,
    setForm,
    step,
    setStep,
    canAdvance,
    errors,
    derived,
    aprPercent,
    setAprPercent,
    reset,
    setFromCounter,
    contingencyOptions,
    leadPaintMode,
    cureCategory,
    termsCategory,
  };
}
